(() => {
  const endpoint = document.documentElement.getAttribute("data-lead-endpoint") || "/api/leads";
  const EMAIL_RE = /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;
  const START_KEY = "reflexEarlyAccessStart";
  const START_SOURCES = new Set(["hero", "ami_section", "footer", "updates"]);
  const SOURCE_ALIASES = {
    homepage_early_access: "footer",
    homepage_ami_early_access: "ami_section",
    updates_signup: "updates",
  };
  const SUCCESS_TEXT =
    "You're in. We'll keep you posted as the next Reflex Radio gets closer to launch.";
  const B2B_SUCCESS_HTML =
    "<strong>Thanks.</strong> We received your inquiry and will be in touch.";
  const PENDING_KEY = "reflexPendingSignupSource";

  const rememberPending = (source) => {
    try {
      sessionStorage.setItem(PENDING_KEY, source);
    } catch {
      window.__reflexPendingSignupSource = source;
    }
  };

  const takePending = () => {
    try {
      const value = sessionStorage.getItem(PENDING_KEY) || "";
      sessionStorage.removeItem(PENDING_KEY);
      return value;
    } catch {
      const value = window.__reflexPendingSignupSource || "";
      window.__reflexPendingSignupSource = "";
      return value;
    }
  };

  const findFormBySource = (source) => {
    return [...document.querySelectorAll("form")].find((form) => signupSource(form) === source) || null;
  };

  const recoverB2bPosted = () => {
    const params = new URLSearchParams(location.search);
    if (params.get("early_access")) return false;
    const section = document.getElementById("business-inquiry");
    if (!section) return false;
    const postedParam = /contact_posted=true/i.test(location.search);
    const postedFlag = !!section.querySelector("[data-b2b-posted]");
    if (!postedParam && !postedFlag) return false;

    let success =
      section.querySelector(":scope > [data-b2b-success], :scope > [data-lead-success]")
      || section.querySelector("[data-b2b-success], [data-lead-success]");
    if (!success) {
      success = document.createElement("p");
      success.className = "lead-success";
      success.setAttribute("data-lead-success", "");
      success.setAttribute("data-b2b-success", "");
      const lede = section.querySelector(".inquiry-lede");
      if (lede) lede.insertAdjacentElement("afterend", success);
      else section.insertAdjacentElement("afterbegin", success);
    }
    success.hidden = false;
    success.removeAttribute("hidden");
    success.setAttribute("data-b2b-success", "");
    success.setAttribute("role", "status");
    if (!success.hasAttribute("tabindex")) success.tabIndex = -1;
    success.innerHTML = B2B_SUCCESS_HTML;

    const form = section.querySelector("#b2b-inquiry-form") || section.querySelector("form.inquiry-form");
    if (form) {
      if (form.contains(success)) {
        form.insertAdjacentElement("beforebegin", success);
      }
      form.setAttribute("hidden", "hidden");
      const fields = form.querySelector("[data-b2b-fields]");
      if (fields) fields.setAttribute("hidden", "hidden");
    }

    const html = document.documentElement;
    const previousBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    section.scrollIntoView({ block: "start", behavior: "auto" });
    html.style.scrollBehavior = previousBehavior;
    if (location.hash !== "#business-inquiry") {
      try {
        history.replaceState(null, "", `${location.pathname}${location.search}#business-inquiry`);
      } catch {
        /* History must never break the native contact return. */
      }
    }
    if (typeof success.focus === "function") {
      try {
        success.focus({ preventScroll: true });
      } catch {
        success.focus();
      }
    }
    return true;
  };

  const recoverPostedSuccess = () => {
    if (recoverB2bPosted()) return;
    const params = new URLSearchParams(location.search);
    const posted = /customer_posted=true|contact_posted=true/i.test(location.search);
    const source = takePending() || params.get("early_access") || "";
    if (!source || (!posted && !params.get("early_access"))) return;
    const form = findFormBySource(source);
    if (form) {
      const liquidSuccess = form.querySelector("[data-lead-success]");
      if (liquidSuccess) {
        liquidSuccess.hidden = false;
        liquidSuccess.tabIndex = -1;
        liquidSuccess.focus();
        form.dataset.leadState = "success";
      } else {
        showSuccess(form);
      }
    }
    trackSignup(form || document.documentElement, source);
  };

  const leadRoot = (form) =>
    form.querySelector("[data-shopify-lead]") || form.closest("[data-shopify-lead]") || form;

  const signupSource = (form) => {
    const raw =
      leadRoot(form).getAttribute("data-signup-source")
      || form.getAttribute("data-signup-source")
      || leadRoot(form).getAttribute("data-lead-source")
      || form.getAttribute("data-lead-source")
      || "";
    return SOURCE_ALIASES[raw] || raw;
  };

  const track = (name, params) => {
    try {
      const queue = window.reflexGa4Queue || (window.reflexGa4Queue = []);
      queue.push({ event: name, params: params || {} });
    } catch {
      /* Analytics must never break signup. */
    }
  };

  const trackStart = (source) => {
    if (!START_SOURCES.has(source)) return;
    try {
      if (sessionStorage.getItem(START_KEY) === "1") return;
      sessionStorage.setItem(START_KEY, "1");
    } catch {
      if (window.__reflexEarlyAccessStart) return;
      window.__reflexEarlyAccessStart = true;
    }
    track("early_access_start", { signup_source: source });
  };

  const trackSignup = (form, source) => {
    if (form?.dataset.signupTracked === "true") return;
    try {
      const key = `reflexSignupTracked:${source}`;
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
    } catch {
      window.__reflexSignupTracked = window.__reflexSignupTracked || {};
      if (window.__reflexSignupTracked[source]) return;
      window.__reflexSignupTracked[source] = true;
    }
    if (form) form.dataset.signupTracked = "true";
    if (!START_SOURCES.has(source)) return;
    track("early_access_signup", { signup_source: source });
  };

  const fieldAliases = {
    first_name: ["first_name", "contact[first_name]"],
    email: ["email", "contact[email]"],
    consent: ["consent", "contact[accepts_marketing]", "contact[consent]"],
    name: ["name", "contact[name]"],
    work_email: ["work_email", "contact[email]"],
    company: ["company", "contact[company]"],
    role: ["role", "contact[role]"],
    company_type: ["company_type", "contact[company_type]"],
    message: ["message", "contact[body]"],
    phone: ["phone", "contact[phone]"],
    website: ["website"],
    company_url: ["company_url"],
  };

  const fieldNode = (form, name) => {
    for (const alias of fieldAliases[name] || [name]) {
      const node = form.querySelector(`[name="${alias}"]`);
      if (node) return node;
    }
    return null;
  };

  const clearFieldErrors = (form) => {
    form.querySelectorAll("[data-field-error]").forEach((node) => {
      node.textContent = "";
      node.hidden = true;
    });
    form.querySelectorAll("[aria-invalid]").forEach((node) => {
      node.removeAttribute("aria-invalid");
    });
  };

  const setState = (form, state, message) => {
    form.dataset.leadState = state;
    const status = form.querySelector("[data-lead-status]");
    if (status) {
      status.textContent = message || "";
      status.hidden = !message;
      status.setAttribute("role", state === "error" ? "alert" : "status");
    }
    const submit = form.querySelector("[type='submit']");
    if (submit) {
      submit.disabled = state === "submitting" || state === "success";
      if (state === "submitting") submit.setAttribute("aria-busy", "true");
      else submit.removeAttribute("aria-busy");
    }
  };

  const fieldError = (form, name, message) => {
    const input = fieldNode(form, name);
    if (input) input.setAttribute("aria-invalid", "true");
    const err = form.querySelector(`[data-field-error="${name}"]`);
    if (err) {
      err.textContent = message;
      err.hidden = false;
    }
  };

  const readConsent = (form) => {
    const box = fieldNode(form, "consent");
    if (!box) return false;
    return box.type === "checkbox" ? box.checked : Boolean(box.value);
  };

  const payloadFrom = (form) => {
    const root = leadRoot(form);
    const kind = root.getAttribute("data-lead-kind") || form.getAttribute("data-lead-kind") || "consumer";
    const source = root.getAttribute("data-lead-source") || form.getAttribute("data-lead-source") || "";
    const data = {
      kind,
      source,
      consent: readConsent(form),
      consent_text: root.getAttribute("data-consent-text") || form.getAttribute("data-consent-text") || "",
      website: fieldNode(form, "website")?.value || "",
      company_url: fieldNode(form, "company_url")?.value || "",
    };
    const fields = kind === "b2b"
      ? ["name", "work_email", "company", "role", "company_type", "message", "phone"]
      : ["email"];
    fields.forEach((name) => {
      const node = fieldNode(form, name);
      if (node) data[name] = node.value.trim();
    });
    return data;
  };

  const clientValidate = (form, data) => {
    const errors = [];
    const emailValue = data.work_email || data.email || "";
    const emailOk = EMAIL_RE.test(emailValue);
    if (data.kind === "consumer") {
      if (!emailOk) errors.push(["email", "Please enter a valid email address."]);
    } else {
      if (!data.name) errors.push(["name", "Please enter your name."]);
      if (!emailOk) errors.push(["work_email", "Please enter a valid work email."]);
      if (!data.company) errors.push(["company", "Please enter your company."]);
      if (!data.role) errors.push(["role", "Please enter your role."]);
      if (!data.company_type) errors.push(["company_type", "Please choose a company type."]);
      if (!data.message || data.message.length < 8) {
        errors.push(["message", "Please tell us briefly how you are thinking about using Reflex."]);
      }
    }
    if (!data.consent) {
      errors.push([
        "consent",
        leadRoot(form).getAttribute("data-consent-error")
          || form.getAttribute("data-consent-error")
          || "Please confirm you want launch updates from Reflex.",
      ]);
    }
    errors.forEach(([name, message]) => fieldError(form, name, message));
    return errors.length === 0;
  };

  const showSuccess = (form, { already } = {}) => {
    let success = form.nextElementSibling;
    if (!success || !success.hasAttribute("data-lead-success")) {
      success = form.parentElement?.querySelector(":scope > [data-lead-success]")
        || form.querySelector("[data-lead-success]");
    }
    if (!success) {
      success = document.createElement("p");
      success.className = "lead-success";
      success.setAttribute("data-lead-success", "");
      success.tabIndex = -1;
      form.insertAdjacentElement("afterend", success);
    }
    success.hidden = false;
    const kind = leadRoot(form).getAttribute("data-lead-kind") || form.getAttribute("data-lead-kind") || "consumer";
    if (kind === "b2b") {
      success.innerHTML = B2B_SUCCESS_HTML;
    } else {
      success.innerHTML = already
        ? "<strong>You're in.</strong> That email is already on the Reflex list."
        : "<strong>You're in.</strong> We'll keep you posted as the next Reflex Radio gets closer to launch.";
    }
    form.setAttribute("hidden", "hidden");
    setState(form, "success", "");
    success.focus();
  };

  const parseShopifyResult = (url, html) => {
    const haystack = `${url}\n${html}`;
    if (/missing captcha|captcha token|failed recaptcha|h-captcha/i.test(haystack)
      && /error|invalid|missing|failed/i.test(haystack)) {
      return { type: "captcha" };
    }
    if (/already been taken|already subscribed|already on the/i.test(html)) {
      return { type: "already" };
    }
    if (/customer_posted=true|contact_posted=true/i.test(haystack)) {
      return { type: "created" };
    }
    if (/please enter a valid email|email is invalid|is invalid/i.test(html)
      && /error|form/i.test(html)) {
      return { type: "invalid" };
    }
    if (/<body/i.test(html) && !/errors\.messages|form__message--error|field--error/i.test(html)) {
      return { type: "created" };
    }
    return { type: "unknown" };
  };

  const submitShopify = async (form) => {
    const source = signupSource(form);
    rememberPending(source);
    setState(form, "submitting", "Sending…");
    try {
      const response = await fetch(form.action || "/contact", {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "text/html" },
        redirect: "follow",
      });
      const html = await response.text();
      const result = parseShopifyResult(response.url || "", html);
      if (result.type === "captcha" || result.type === "unknown") {
        setState(form, "submitting", "Sending…");
        form.dataset.nativeFallback = "true";
        form.submit();
        return;
      }
      if (result.type === "invalid") {
        fieldError(form, "email", "Please enter a valid email address.");
        setState(form, "error", "Please check the highlighted fields.");
        fieldNode(form, "email")?.focus();
        return;
      }
      if (result.type === "already") {
        takePending();
        showSuccess(form, { already: true });
        trackSignup(form, source);
        return;
      }
      takePending();
      showSuccess(form);
      trackSignup(form, source);
    } catch {
      setState(
        form,
        "error",
        "We couldn't reach Reflex just now. Check your connection and try again."
      );
    }
  };

  const submitLocal = async (form) => {
    const data = payloadFrom(form);
    const source = signupSource(form);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(data),
      });
      let body = {};
      try {
        body = await response.json();
      } catch {
        body = {};
      }
      if (response.ok && body.ok) {
        form.reset();
        showSuccess(form);
        trackSignup(form, source);
        return;
      }
      const code = body.code || "";
      const message = body.message || "Something went wrong. Please try again.";
      if (response.status >= 400 && response.status < 500 && code !== "rate_limited") {
        const map = {
          invalid_email: data.kind === "b2b" ? "work_email" : "email",
          invalid_name: "name",
          invalid_company: "company",
          invalid_role: "role",
          invalid_company_type: "company_type",
          invalid_message: "message",
          invalid_phone: "phone",
          consent_required: "consent",
        };
        if (map[code]) fieldError(form, map[code], message);
        setState(form, "error", message);
        return;
      }
      setState(
        form,
        "error",
        "We couldn't send that just now. Please try again, or write contactus@reflex-radio.com."
      );
    } catch {
      setState(
        form,
        "error",
        "We couldn't reach Reflex just now. Check your connection and try again."
      );
    }
  };

  const bindForm = (form) => {
    if (form.dataset.earlyAccessBound === "true") return;
    form.dataset.earlyAccessBound = "true";
    form.setAttribute("novalidate", "novalidate");
    const source = signupSource(form);
    const kind = leadRoot(form).getAttribute("data-lead-kind") || form.getAttribute("data-lead-kind") || "consumer";
    const shopify = (leadRoot(form).getAttribute("data-provider") || form.getAttribute("data-provider")) === "shopify";

    if (kind === "consumer") {
      const email = fieldNode(form, "email");
      if (email) {
        email.addEventListener("focus", () => trackStart(source));
        email.addEventListener("input", () => {
          if (email.value.trim()) trackStart(source);
        });
      }
    }

    form.addEventListener("submit", (event) => {
      if (form.dataset.leadState === "success") {
        event.preventDefault();
        return;
      }
      if (form.dataset.leadState === "submitting") {
        if (event.isTrusted) event.preventDefault();
        return;
      }
      const data = payloadFrom(form);
      clearFieldErrors(form);
      if (!clientValidate(form, data)) {
        event.preventDefault();
        setState(form, "error", "Please check the highlighted fields.");
        const firstInvalid = form.querySelector("[aria-invalid='true']");
        if (firstInvalid) firstInvalid.focus();
        return;
      }
      if (kind === "consumer") trackStart(source);
      if (shopify && kind === "consumer") {
        // Native POST so Shopify CAPTCHA can attach its token. AJAX omits it
        // and a programmatic form.submit() bypasses the challenge widget.
        rememberPending(source);
        form.dataset.leadState = "submitting";
        const status = form.querySelector("[data-lead-status]");
        if (status) {
          status.textContent = "Sending…";
          status.hidden = false;
          status.setAttribute("role", "status");
        }
        return;
      }
      if (shopify) {
        setState(form, "submitting", "Sending…");
        return;
      }
      event.preventDefault();
      setState(form, "submitting", "Sending…");
      submitLocal(form);
    });
  };

  document.querySelectorAll("[data-early-access-start]").forEach((link) => {
    link.addEventListener("click", () => {
      trackStart(link.getAttribute("data-early-access-start") || "");
    });
  });

  const forms = new Set([
    ...document.querySelectorAll("[data-lead-form]"),
    ...[...document.querySelectorAll("[data-shopify-lead]")].map((node) => node.closest("form")).filter(Boolean),
  ]);
  forms.forEach((form) => {
    setState(form, "initial", leadRoot(form).getAttribute("data-initial-status") || form.getAttribute("data-initial-status") || "");
    bindForm(form);
  });
  recoverPostedSuccess();
})();
